import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { alpha } from '@mui/material/styles';

/** Marks intentional white/print document areas that keep light backgrounds in dark mode. */
export const PHC_DOCUMENT_SURFACE_CLASS = 'phc-document-surface';
// Applied on ExecutiveSummary and ImpressionEditor document canvases.

export type DocumentViewMode =
  | 'expanded'
  | 'editing'
  | 'compact'
  | 'print'
  | 'presentation-expanded'
  | 'presentation-compact';

export const usesWhiteDocumentSurface = (viewMode: DocumentViewMode) =>
  viewMode === 'print' ||
  viewMode === 'presentation-expanded' ||
  viewMode === 'presentation-compact';

export const getEditorCanvasBackground = (
  theme: Theme,
  viewMode: DocumentViewMode,
) => {
  if (
    viewMode === 'presentation-expanded' ||
    viewMode === 'presentation-compact'
  ) {
    return alpha(theme.palette.common.white, 0.95);
  }
  if (viewMode === 'print') {
    return theme.palette.common.white;
  }
  return theme.palette.mode === 'dark'
    ? theme.palette.background.paper
    : theme.palette.common.white;
};

export const getDocumentSurfaceTextColor = (theme: Theme) =>
  theme.palette.black[900];

export const getDocumentSurfaceSecondaryTextColor = (theme: Theme) =>
  theme.palette.black[600];

export const useDocumentSurfaceStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.common.white,
    ...(theme.palette.mode === 'dark' && {
      color: getDocumentSurfaceTextColor(theme),
      // ChromaText uses theme text colors globally; restore readable contrast on white.
      '& [class*="ChromaText-"]': {
        color: getDocumentSurfaceTextColor(theme),
      },
      '& [class*="ChromaText-caption"], & [class*="ChromaText-subbody"]': {
        color: getDocumentSurfaceSecondaryTextColor(theme),
      },
      '& [class*="ChromaTextField-label"]': {
        color: theme.palette.black[800],
      },
      '& [class*="ChromaTextField-input"]': {
        backgroundColor: alpha(theme.palette.black[900], 0.06),
        color: getDocumentSurfaceTextColor(theme),
      },
      '& [class*="ChromaSearchField-input"]': {
        backgroundColor: alpha(theme.palette.black[900], 0.06),
        color: getDocumentSurfaceTextColor(theme),
      },
      // Higher specificity than MarkdownEditor's direct contenteditable class.
      '&& .es-details-summary [contenteditable="true"]': {
        color: getDocumentSurfaceTextColor(theme),
      },
      '&& .es-details-summary [contenteditable="true"] a': {
        color: theme.palette.blue[700],
      },
      '&& .es-details-summary .MarkdownEditorTheme__tableCell, && .es-details-summary .MarkdownEditorTheme__tableCellHeader':
        {
          color: getDocumentSurfaceTextColor(theme),
        },
    }),
  },
}));
